<script setup>
import { ref, computed, watch } from 'vue';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { Link, usePage, Head } from '@inertiajs/vue3';
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverGroup, PopoverPanel, TransitionChild, TransitionRoot, } from '@headlessui/vue'
import Paginator from 'primevue/paginator';
import moment from 'moment';

const page = usePage();

const props = defineProps({ bids: Array })

let eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const sortOptions = [
    { name: 'Afgelopen 30 dagen', href: '?sort=30' },
    { name: 'Afgelopen 60 dagen', href: '?sort=60' },
    { name: 'Afgelopen 90 dagen', href: '?sort=90' },
    { name: '90 dagen of ouder', href: '?sort=91' },
    { name: 'Alle data', href: '?sort=all' }
]

function getCurrentSortOption() {
    //get url sort parameter
    let urlParams = new URLSearchParams(window.location.search);
    let sort = urlParams.get('sort');
    if (sort) {
        let sortOption = sortOptions.find(option => option.href.includes(sort));
        return sortOption.name;
    } else {
        return 'Afgelopen 30 dagen';
    }
}

const pageBids = computed(() => page.props.bids);

watch(pageBids, () => {
    bids.value = pageBids.value;
    bids.value = doFilter();
    //set paginator to page 1
    if (paginator.value) {
        paginator.value.changePage(0);
    }
})

const bids = ref(props.bids);
const paginator = ref(null);
const paginatedData = ref([]);
const searchValue = ref('');

watch(searchValue, (newValue) => {
    bids.value = doFilter();
});

watch(bids, () => {
    if (paginator.value) {
        paginator.value.changePage(0);
    }
    changePagination({ rows: 10, page: 0 });
});

function changePagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;
    //get the data for the current page

    paginatedData.value = bids.value.slice(startIndex, endIndex);
}

changePagination({ rows: 10, page: 0 });

function doFilter() {
  const filteredBids = props.bids.filter(order => {
    if (activeStatusFilters.value.length > 0) {
        //if has high, check if user has highest bid
        if (activeStatusFilters.value.includes('high')) {
            if (order.amount === order.lot.highest_bid) {
                return true;
            }
        }
        //if has overbid, check if user has been overbid
        if (activeStatusFilters.value.includes('overbid')) {
            if (order.lot.highest_bid > order.amount) {
                return true;
            }
        }

        //if has won, check if user has highest bid and the lot is closed
        if (activeStatusFilters.value.includes('won')) {
            //check if highest bid and lot has been created over 5 days ago
            if (order.amount === order.lot.highest_bid && moment(order.lot.created_at).add(5, 'days') < moment()) {
                return true;
            }
        }

        //if has lost, check if user has been overbid and the lot is closed
        if (activeStatusFilters.value.includes('lost')) {
            //check if overbid and lot has been created over 5 days ago
            if (order.amount !== order.lot.highest_bid && moment(order.lot.created_at).add(5, 'days') < moment()) {
                return true;
            }
        }
        return false;
    } else {
        return true;
    }
  });

  if (searchValue.value) {
    return filteredBids.filter(bids => {
      //check date
        return bids.lot.make.toLowerCase().includes(searchValue.value.toLowerCase()) || bids.lot.model.toLowerCase().includes(searchValue.value.toLowerCase());
    });
  }

  return filteredBids;
}

const activeStatusFilters = ref([]);

function getModel(sectionId) {
    switch (sectionId) {
        case 'status':
            return activeStatusFilters
    }
}

const filters = [
    {
        id: 'status',
        name: 'Status',
        options: [
            { value: 'high', label: 'Hoogste bod' },
            { value: 'overbid', label: 'Overboden' },
            { value: 'won', label: 'Gewonnen' },
            { value: 'lost', label: 'Verloren' },
        ],
    },
]
const mobileFiltersOpen = ref(false)

watch(activeStatusFilters, () => {
    bids.value = doFilter();
});

function getRemainingTime(endDateTime) {
    const end = moment(endDateTime);

    const now = moment();

    const duration = moment.duration(end.diff(now));

    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    if (days > 0) {
        return `${days}d` + (hours > 0 ? ` ${hours}h` : '') + (minutes > 0 ? ` ${minutes}m` : '');
    } else if (hours > 0) {
        return `${hours}h` + (minutes > 0 ? ` ${minutes}m` : '');
    } else if (minutes > 0) {
        return `${minutes}m`;
    } else {
        return 'Veiling afgelopen';
    }
}
</script>

<template>
    <Head title="Biedingen | Mijnautosnelweg" />
    <main class="px-4 sm:px-6 py-6 space-y-8">
        <div class="">
            <nav class="sm:hidden" aria-label="Back">
                <Link href="/portal/dashboard" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 gap-x-1">
                    <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-3 w-3 text-gray-400 shrink-0" />
                    Terug
                </Link>
            </nav>
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
                <ol role="list" class="flex items-center space-x-4">
                    <li>
                        <div class="flex">
                            <Link href="/portal/dashboard"
                                class="text-sm font-medium text-gray-500 hover:text-gray-700">Dashboard
                            </Link>
                        </div>
                    </li>
                    <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-3 w-3 text-gray-400 shrink-0" />
                    <li>
                        <div class="flex items-center">
                            <Link href="#" class="text-sm font-medium text-gray-900 hover:text-gray-700">
                            Biedingen</Link>
                        </div>
                    </li>
                </ol>
            </nav>
        </div>
        <div class="flex flex-col sm:flex-row justify-between w-full gap-y-2">
            <div class="flex flex-col">
                <h1 class="text-base font-semibold leading-6 text-gray-900">Mijn biedingen</h1>
            </div>
            <div class="flex flex-row gap-3 items-center">
                <IconField iconPosition="right" class="w-full sm:w-auto">
                    <InputIcon class="pi pi-search"></InputIcon>
                    <InputText class="h-10" placeholder="Zoeken" v-model="searchValue" />
                </IconField>
            </div>
        </div>
        <div>
            <TransitionRoot as="template" :show="mobileFiltersOpen">
                <Dialog class="relative z-40 xl:hidden" @close="mobileFiltersOpen = false">
                    <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                        enter-from="opacity-0" enter-to="opacity-100"
                        leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                        leave-to="opacity-0">
                        <div class="fixed left-0 right-0 top-0 bottom-0 bg-black bg-opacity-5" />
                    </TransitionChild>
                    <div class="fixed left-0 right-0 top-0 bottom-0 z-40 flex">
                        <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                            enter-from="translate-x-full" enter-to="translate-x-0"
                            leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                            leave-to="translate-x-full">
                            <DialogPanel
                                class="relative ml-auto flex h-full w-full max-w-xs bg-white flex-col overflow-y-auto  py-4 pb-6 shadow-xl">
                                <div class="flex items-center justify-between px-4">
                                    <h2 class="text-lg font-medium text-gray-900">Filters</h2>
                                    <button type="button"
                                        class="-mr-2 flex h-10 w-10 items-center justify-center rounded-md  p-2 text-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brightorange"
                                        @click="mobileFiltersOpen = false">
                                        <span class="sr-only">Close menu</span>
                                        <font-awesome-icon :icon="['far', 'xmark']" class="h-6 w-6 shrink-0"/>
                                    </button>
                                </div>
                                <form class="mt-4">
                                    <Disclosure as="div" v-for="section in filters" :key="section.name"
                                        class="border-t border-gray-200 px-4 py-6" v-slot="{ open }">
                                        <h3 class="-mx-2 -my-3 flow-root">
                                            <DisclosureButton
                                                class="flex w-full items-center justify-between  px-2 py-3 text-sm text-gray-400">
                                                <span class="font-medium text-gray-900">{{ section.name }}</span>
                                                <span class="ml-6 flex items-center">
                                                    <font-awesome-icon :icon="['far', 'chevron-down']" rotation="90" :class="{ 'h-4 w-4 text-royalblue-500 fa-rotate': open, 'hidden': !open }" aria-hidden="true" />
                                                    <font-awesome-icon :icon="['far', 'chevron-right']" rotation="180" :class="{ 'hidden': open, 'h-4 w-4 text-royalblue-500': !open }" aria-hidden="true" />
                                                </span>
                                            </DisclosureButton>
                                        </h3>
                                        <DisclosurePanel class="pt-6">
                                            <div class="space-y-2">
                                                <div v-for="(option, optionIdx) in section.options" :key="option.value"
                                                    class="flex items-center">
                                                    <input :id="`filter-mobile-${section.id}-${optionIdx}`" v-model="getModel(section.id).value"
                                                    :value="option.value" :checked="getModel(section.id).value.includes(option.value)" type="checkbox" 
                                                        class="h-4 w-4 ring-1 ring-inset ring-gray-300 rounded border-gray-300 text-brightorange focus:ring-brightorange" />
                                                    <label :for="`filter-mobile-${section.id}-${optionIdx}`"
                                                        class="ml-3 text-sm text-gray-500">{{ option.label }}</label>
                                                </div>
                                            </div>
                                        </DisclosurePanel>
                                    </Disclosure>
                                </form>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </TransitionRoot>

            <section aria-labelledby="filter-heading">
                <h2 id="filter-heading" class="sr-only">Product filters</h2>

                <div class="flex items-center justify-between">

                    <button type="button"
                        class="ring-1 ring-inset ring-gray-300 rounded-lg py-3.5 px-2.5 flex flex-row gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 xl:hidden"
                        @click="mobileFiltersOpen = true">
                        <font-awesome-icon :icon="['far', 'filter']" class="h-5 w-5 shrink-0"/>

                        Filters
                    </button>

                    <PopoverGroup class="hidden xl:flex md:items-baseline sm:space-x-8">
                        <Popover as="div" v-for="(section, sectionIdx) in filters" :key="section.name" id="menu"
                            class="relative inline-block text-left">
                            <PopoverButton
                                class="group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                <span>{{ section.name }}</span>
                                <span v-if="getModel(section.id).value.length > 0"
                                    class="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">{{getModel(section.id).value.length}}</span>
                                <font-awesome-icon :icon="['far', 'chevron-right']" rotation="90" class="h-4 w-4 text-royalblue-500" aria-hidden="true" />
                            </PopoverButton>

                            <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                                <PopoverPanel
                                    class="absolute max-h-64 overflow-scroll bg-white border z-10 mt-2 origin-top-right rounded-md  p-4 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <form class="space-y-4">
                                        <div v-for="(option, optionIdx) in section.options" :key="option.value" 
                                            class="flex items-center">
                                            <input :id="`filter-${section.id}-${optionIdx}`" v-model="getModel(section.id).value"
                                                :value="option.value" :checked="getModel(section.id).value.includes(option.value)" type="checkbox" 
                                                class="h-4 w-4 ring-1 ring-inset ring-gray-300 rounded border-gray-300 text-brightorange focus:ring-brightorange" />
                                            <label :for="`filter-${section.id}-${optionIdx}`"
                                                class="ml-3 whitespace-nowrap pr-6 text-sm font-medium text-gray-900">{{
                                                option.label
                                                }}</label>
                                        </div>
                                    </form>
                                </PopoverPanel>
                            </transition>
                        </Popover>
                    </PopoverGroup>


                    <Menu as="div" class="relative inline-block text-left">
                        <div class="flex flex-row gap-2 items-center">

                            <MenuButton
                                class="group inline-flex justify-center items-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                {{getCurrentSortOption()}}
                                <font-awesome-icon :icon="['fal', 'sort']" class="h-4 w-4 text-royalblue-500" aria-hidden="true" />
                            </MenuButton>
                        </div>

                        <transition enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95">
                            <MenuItems
                                class="absolute bg-white right-0 border z-10 mt-2 w-48 origin-top-left rounded-md  shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div class="py-1">
                                    <MenuItem v-for="option in sortOptions" :key="option"
                                        v-slot="{ sortOptionSelected }">
                                    <Link :href="option.href"
                                        :class="[sortOptionSelected ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm font-medium text-gray-900']">{{
                                        option.name
                                        }}</Link>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
            </section>
            <div class="space-y-8" v-if="bids.length > 0">
                <div class="mt-10 ring-1 ring-gray-300 mx-0 rounded-lg">
                    <table class="min-w-full divide-y divide-gray-300 border-b">
                        <thead>
                            <tr>
                                <th scope="col"
                                    class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                    Kavelnummer
                                </th>
                                <th scope="col"
                                    class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                    Loopt af over
                                </th>
                                <th scope="col"
                                    class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                    Kavel
                                </th>
                                <th scope="col"
                                    class="table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                    Mijn bod
                                </th>
                                <th scope="col"
                                    class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                    Hoogste bod
                                </th>
                                <th scope="col"
                                    class="hidden lg:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                    Indicatieprijs
                                </th>
                            </tr>
                        </thead>
                        <tbody class="divide-y">
                            <tr v-for="bid in paginatedData">
                                <td class="whitespace-nowrap hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                                    {{bid.lot_id}}
                                </td>
                                <td class="whitespace-nowrap hidden sm:table-cell px-3 py-4 text-sm text-gray-500">
                                    {{getRemainingTime(bid.lot.auction_end_date)}}
                                </td>
                                <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-gray-500 max-w-32 sm:max-w-40 overflow-clip overflow-ellipsis">
                                    {{bid.lot.make}} {{bid.lot.model}} {{bid.lot.construction_year}}
                                </td>
                                <td class="whitespace-nowrap table-cell px-3 py-4 text-sm text-red-500">
                                    {{eurFormatter.format(bid.amount)}}</td>
                                <td class="whitespace-nowrap hidden sm:table-cell px-3 py-4 text-sm text-green-500">
                                    {{eurFormatter.format(bid.lot.highest_bid)}} </td>
                                <td class="whitespace-nowrap hidden lg:table-cell px-3 py-4 text-sm text-gray-500">
                                    {{eurFormatter.format(bid.lot.price_indication)}}
                                </td>
                                <td
                                    class="hidden lg:flex whitespace-nowrap py-4 pr-4 pl-3 text-right text-sm font-medium gap-1.5">
                                    <Link :href="'/portal/kavel/'+bid.lot_id" class="text-royalblue-500 hover:text-royalblue-500/80 ml-auto">
                                    Bekijk kavel</Link>
                                </td>
                                <td class="flex lg:hidden whitespace-nowrap py-4 text-sm gap-1.5 pr-3">
                                    <Link href="" class="text-royalblue-500 hover:text-royalblue-500/80 ml-auto">
                                    <font-awesome-icon :icon="['far', 'eye']" class="text-royalblue-500 hover:text-royalblue-500/80 h-5 w-5 shrink-0"/>
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <Paginator ref="paginator" v-if="bids.length > 10" @page="changePagination" :rows="10"
                    :totalRecords="bids.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>
            </div>
            <div v-else>
                <div class="flex flex-col items-center justify-center h-96">
                    <font-awesome-icon :icon="['far', 'tag']" class="h-12 w-12 text-gray-400 shrink-0"/>
                    <p class="mt-4 text-sm font-semibold text-gray-900">Geen biedingen gevonden</p>
                </div>
            </div>
        </div>
    </main>
</template>
